import { useState, useEffect } from "react";
import { api } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Users, Search, Shield, User, UserCog, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [roleFilter, setRoleFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "staff"
  });

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const url = roleFilter === "all" 
        ? "/admin/users?limit=100" 
        : `/admin/users?role=${roleFilter}&limit=100`;
      const response = await api.get(url);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdating(true);
    try {
      await api.put(`/admin/users/${userId}/role?role=${newRole}`);
      toast.success("User role updated");
      fetchUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error("Failed to update role");
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (userId, isActive) => {
    setUpdating(true);
    try {
      await api.put(`/admin/users/${userId}/status?is_active=${isActive}`);
      toast.success(`User ${isActive ? 'activated' : 'deactivated'}`);
      fetchUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setCreating(true);
    try {
      await api.post("/admin/users/create", newUser);
      toast.success("User created");
      setCreateDialogOpen(false);
      setNewUser({ name: "", email: "", password: "", role: "staff" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return <Shield size={16} className="text-[#D4AF37]" />;
      case 'staff': return <UserCog size={16} className="text-[#60A5FA]" />;
      default: return <User size={16} className="text-[#A0A0A0]" />;
    }
  };

  const getRoleBadge = (role) => {
    switch(role) {
      case 'admin': return 'badge-gold';
      case 'staff': return 'bg-[#60A5FA]/15 text-[#60A5FA]';
      default: return 'bg-[#A0A0A0]/15 text-[#A0A0A0]';
    }
  };

  const filteredUsers = (users || []).filter(user => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return user.name?.toLowerCase().includes(term) || 
           user.email?.toLowerCase().includes(term);
  });

  return (
    <AdminLayout title="Users & Roles">
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="pl-10 bg-[#131316] border-[#2A2A2F] text-white focus:border-[#D4AF37] w-64"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-32 bg-[#131316] border-[#2A2A2F] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131316] border-[#2A2A2F]">
                <SelectItem value="all" className="text-white">All Roles</SelectItem>
                <SelectItem value="customer" className="text-white">Customers</SelectItem>
                <SelectItem value="staff" className="text-white">Staff</SelectItem>
                <SelectItem value="admin" className="text-white">Admins</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[#D4AF37] text-[#0A0A0B]"
            data-testid="create-user-btn"
          >
            <Plus size={18} className="mr-2" /> Add Staff/Admin
          </Button>
        </div>

        <p className="text-[#A0A0A0] text-sm">{filteredUsers.length} users</p>

        {/* Users List */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-20 rounded-xl animate-shimmer" />
            ))}
          </div>
        ) : filteredUsers.length > 0 ? (
          <div className="space-y-3">
            {filteredUsers.map((user) => (
              <Card 
                key={user.id} 
                className={`bg-[#131316] border-[#2A2A2F] cursor-pointer hover:border-[#D4AF37]/50 transition-colors ${
                  !user.is_active ? 'opacity-60' : ''
                }`}
                onClick={() => setSelectedUser(user)}
                data-testid={`user-row-${user.id}`}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#1A1A1E] flex items-center justify-center">
                      {getRoleIcon(user.role)}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-[#6B6B6B] text-sm">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`badge ${getRoleBadge(user.role)}`}>
                      {user.role}
                    </span>
                    {!user.is_active && (
                      <span className="badge badge-error">Inactive</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users size={64} className="mx-auto mb-4 text-[#6B6B6B] opacity-50" />
            <h3 className="text-white text-xl font-semibold mb-2">No Users Found</h3>
            <p className="text-[#A0A0A0]">Try adjusting your filters</p>
          </div>
        )}

        {/* User Edit Dialog */}
        <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
          <DialogContent className="bg-[#131316] border-[#2A2A2F] text-white">
            <DialogHeader>
              <DialogTitle>Manage User</DialogTitle>
            </DialogHeader>
            
            {selectedUser && (
              <div className="space-y-4">
                <div className="bg-[#0A0A0B] rounded-lg p-4">
                  <p className="text-white font-medium">{selectedUser.name}</p>
                  <p className="text-[#6B6B6B] text-sm">{selectedUser.email}</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[#A0A0A0] text-sm">Role</label>
                  <Select
                    value={selectedUser.role}
                    onValueChange={(value) => handleRoleChange(selectedUser.id, value)}
                    disabled={updating}
                  >
                    <SelectTrigger className="bg-[#1A1A1E] border-[#2A2A2F] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1E] border-[#2A2A2F]">
                      <SelectItem value="customer" className="text-white">Customer</SelectItem>
                      <SelectItem value="staff" className="text-white">Staff</SelectItem>
                      <SelectItem value="admin" className="text-white">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="pt-4 border-t border-[#2A2A2F]">
                  <Button
                    onClick={() => handleStatusChange(selectedUser.id, !selectedUser.is_active)}
                    disabled={updating}
                    variant="outline"
                    className={selectedUser.is_active 
                      ? "border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white w-full"
                      : "border-[#4ADE80] text-[#4ADE80] hover:bg-[#4ADE80] hover:text-[#0A0A0B] w-full"
                    }
                  >
                    {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : 
                      (selectedUser.is_active ? 'Deactivate Account' : 'Activate Account')
                    }
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="bg-[#131316] border-[#2A2A2F] text-white">
            <DialogHeader>
              <DialogTitle>Create Staff/Admin User</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Name</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Full name"
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="new-user-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Email</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="email@example.com"
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="new-user-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Password</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Min 6 characters"
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="new-user-password"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger className="bg-[#1A1A1E] border-[#2A2A2F] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1E] border-[#2A2A2F]">
                    <SelectItem value="staff" className="text-white">Staff</SelectItem>
                    <SelectItem value="admin" className="text-white">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                className="border-[#2A2A2F] text-white hover:bg-[#1A1A1E]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={creating}
                className="bg-[#D4AF37] text-[#0A0A0B]"
                data-testid="submit-create-user"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
